'use client'

import {
  createContext,
  use,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { ThemeProvider as NextThemesProvider, useTheme as useNextTheme } from 'next-themes'
import { IconContext } from '@/components/icons'
import { TooltipProvider } from '@/components/ui/tooltip'
import { DEFAULT_THEME, THEME_STORAGE_KEY } from '@/lib/theme'

type Theme = 'light' | 'dark' | 'system'
type ResolvedTheme = 'light' | 'dark'

interface ThemeProviderProps {
  children: ReactNode
  nonce?: string
}

interface ThemeContextValue {
  theme: Theme
  setTheme: (theme: Theme) => void
  resolvedTheme: ResolvedTheme
}

type ForcedThemeContextValue = (theme: ResolvedTheme | undefined) => void

const ForcedThemeContext = createContext<ForcedThemeContextValue | null>(null)

const subscribeToHydration = () => () => undefined

/*
  next-themes renders its no-flash init script from inside the client component
  tree, and React 19 logs a dev-only console error every time it *creates* a
  <script> host instance on the client:

    "Encountered a script tag while rendering React component. Scripts inside
     React components are never executed when rendering on the client."

  That happens whenever the tree under <body> is client-rendered instead of
  hydrated (any hydration bail-out, e.g. a browser extension or translator
  mutating the DOM before hydration). The warning names theme-provider.tsx, so
  it reads as a fatal theming failure while the real cause is elsewhere — it was
  an active red herring during PROD-8459 debugging.

  A client-created script never executes, so the client copy is dead weight
  either way. Tagging it with a non-executable `type` on the client only keeps
  React from flagging it (react-dom `isScriptDataBlock`) while the server-
  rendered copy stays a plain executable script, so no-flash theming is
  untouched. next-themes uses the same server/client split for `nonce`.
*/
const CLIENT_INERT_SCRIPT_PROPS = { type: 'application/json' } as const

function themeScriptProps() {
  return typeof window === 'undefined' ? undefined : CLIENT_INERT_SCRIPT_PROPS
}

// Forced-theme segments (owner portal) mark the document before first paint
// with an inline script; the initializer reads that marker so the first client
// render is already forced without a request-time header read in the root
// layout (which would block the static shell under Cache Components).
function readPrePaintForcedTheme(): ResolvedTheme | undefined {
  if (typeof document === 'undefined') return undefined
  const value = document.documentElement.dataset.trellisForcedTheme
  return isResolvedTheme(value) ? value : undefined
}

export function ThemeProvider({ children, nonce }: ThemeProviderProps) {
  const [forcedTheme, setForcedTheme] = useState<ResolvedTheme | undefined>(readPrePaintForcedTheme)

  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme={DEFAULT_THEME}
      enableColorScheme
      enableSystem
      forcedTheme={forcedTheme}
      nonce={nonce}
      scriptProps={themeScriptProps()}
      storageKey={THEME_STORAGE_KEY}
    >
      <ForcedThemeContext value={setForcedTheme}>
        <IconContext value={{ weight: 'regular', size: 16 }}>
          <TooltipProvider>{children}</TooltipProvider>
        </IconContext>
      </ForcedThemeContext>
    </NextThemesProvider>
  )
}

export function useForcedTheme(theme: ResolvedTheme) {
  const updateForcedTheme = use(ForcedThemeContext)

  useLayoutEffect(() => {
    if (!updateForcedTheme) return

    updateForcedTheme(theme)
    return () => updateForcedTheme(undefined)
  }, [theme, updateForcedTheme])
}

function isTheme(value: string | null | undefined): value is Theme {
  return value === 'light' || value === 'dark' || value === 'system'
}

function isResolvedTheme(value: string | undefined): value is ResolvedTheme {
  return value === 'light' || value === 'dark'
}

export function useTheme(): ThemeContextValue {
  const nextTheme = useNextTheme()
  const hydrated = useSyncExternalStore(
    subscribeToHydration,
    () => true,
    () => false
  )
  const theme = hydrated && isTheme(nextTheme.theme) ? nextTheme.theme : DEFAULT_THEME
  const resolvedTheme =
    hydrated && isResolvedTheme(nextTheme.resolvedTheme) ? nextTheme.resolvedTheme : 'light'
  const setTheme = (value: Theme) => nextTheme.setTheme(value)

  return { theme, setTheme, resolvedTheme }
}
